import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

const PhotographerSignup = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=mobile, 2=otp, 3=profile
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(mobile)) { setError('Enter a valid 10-digit mobile number.'); return; }
    setError(''); setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/auth/send-otp`, { mobile_number: mobile, purpose: 'signup' });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP.');
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 4) { setError('Enter the 4-digit OTP.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/auth/verify-otp`, { mobile_number: mobile, otp, purpose: 'signup' });
      setOtpToken(res.data.token);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP.');
    } finally { setLoading(false); }
  };

  const submitProfile = async () => {
    if (!form.name || !form.email || !form.password) { setError('All fields are required.'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/auth/signup`, {
        otp_token: otpToken,
        name: form.name,
        email: form.email,
        password: form.password
      });
      // Auto-login
      const { token, admin } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('admin', JSON.stringify(admin));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      navigate('/admin/events');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <div style={s.logo}>📷</div>
          <h1 style={s.title}>Photographer Sign Up</h1>
          <div style={s.steps}>
            {['Mobile', 'Verify OTP', 'Profile'].map((label, i) => (
              <div key={i} style={{ ...s.step, ...(step === i + 1 ? s.stepActive : step > i + 1 ? s.stepDone : {}) }}>
                <div style={s.stepDot}>{step > i + 1 ? '✓' : i + 1}</div>
                <span style={s.stepLabel}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        {step === 1 && (
          <div style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Mobile Number</label>
              <input style={s.input} type="tel" placeholder="9876543210" value={mobile}
                onChange={e => setMobile(e.target.value)} maxLength={10} />
              <p style={s.hint}>OTP will be sent to your WhatsApp</p>
            </div>
            <button style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }} onClick={sendOtp} disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div style={s.form}>
            <p style={s.otpMsg}>OTP sent to WhatsApp: <strong>+91 {mobile}</strong></p>
            <div style={s.field}>
              <label style={s.label}>Enter OTP</label>
              <input style={{ ...s.input, letterSpacing: '0.5em', fontSize: '1.5rem', textAlign: 'center' }}
                type="text" inputMode="numeric" placeholder="••••" value={otp}
                onChange={e => setOtp(e.target.value)} maxLength={4} />
            </div>
            <button style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }} onClick={verifyOtp} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button style={s.secondaryBtn} onClick={() => { setStep(1); setOtp(''); setError(''); }}>
              Change Number
            </button>
          </div>
        )}

        {step === 3 && (
          <div style={s.form}>
            {[
              { label: 'Full Name', key: 'name', type: 'text', placeholder: 'Your full name' },
              { label: 'Email', key: 'email', type: 'email', placeholder: 'you@example.com' },
              { label: 'Password', key: 'password', type: 'password', placeholder: '8+ characters' },
              { label: 'Confirm Password', key: 'confirmPassword', type: 'password', placeholder: 'Repeat password' },
            ].map(({ label, key, type, placeholder }) => (
              <div key={key} style={s.field}>
                <label style={s.label}>{label}</label>
                <input style={s.input} type={type} placeholder={placeholder}
                  value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
              </div>
            ))}
            <button style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }} onClick={submitProfile} disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>
        )}

        <p style={s.loginLink}>Already have an account? <Link to="/login" style={s.link}>Sign in</Link></p>
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  card: { background: '#fff', borderRadius: '1.5rem', padding: '2.5rem', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { textAlign: 'center', marginBottom: '2rem' },
  logo: { fontSize: '2.5rem', marginBottom: '0.5rem' },
  title: { fontSize: '1.5rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 1.25rem' },
  steps: { display: 'flex', justifyContent: 'center', gap: '0.5rem' },
  step: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', opacity: 0.4 },
  stepActive: { opacity: 1 },
  stepDone: { opacity: 0.7 },
  stepDot: { width: 28, height: 28, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 },
  stepLabel: { fontSize: '0.7rem', color: '#555', fontWeight: 600 },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.75rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', outline: 'none' },
  hint: { fontSize: '0.75rem', color: '#999', margin: '0.25rem 0 0' },
  otpMsg: { color: '#555', fontSize: '0.9rem', textAlign: 'center', margin: 0 },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' },
  btn: { padding: '0.875rem', background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  secondaryBtn: { padding: '0.75rem', background: 'transparent', color: '#666', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' },
  loginLink: { textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: '#888' },
  link: { color: '#4f46e5', fontWeight: 600, textDecoration: 'none' }
};

export default PhotographerSignup;
